'use client';

import { BoutiqueListingCard } from '@/components/boutiques/BoutiqueListingCard';
import type { BoutiqueCountryOption } from '@/lib/boutique/countryLabels';
import type { StoreHit } from '@/lib/algolia/stores';
import { getLanguageFromLocale } from '@/lib/i18n/config';
import { useLocale, useTranslations } from 'next-intl';

import styles from './BoutiquesList.module.scss';

export interface BoutiquesListProps {
  stores: StoreHit[];
  countryCode: string;
  countryOptions: BoutiqueCountryOption[];
}

/**
 * Boutiques store grid (CMS Placeholder type "boutiques list" on gallet-stores Page).
 */
export function BoutiquesList({
  stores,
  countryCode,
  countryOptions,
}: Readonly<BoutiquesListProps>) {
  const locale = useLocale();
  const localeSlug = getLanguageFromLocale(locale);
  const t = useTranslations('boutiquesPage');

  const countryLabel =
    countryOptions.find((option) => option.code === countryCode)?.label ?? countryCode;

  return (
    <section className={styles.section} aria-labelledby="boutiques-results-heading">
      <div className={styles.results}>
        <h2 id="boutiques-results-heading" className={styles.visuallyHidden}>
          {t('resultsHeading', { country: countryLabel })}
        </h2>

        {stores.length > 0 ? (
          <ul className={styles.grid}>
            {stores.map((store, index) => (
              <li key={store.objectID} className={styles.gridItem}>
                <BoutiqueListingCard
                  store={store}
                  localeSlug={localeSlug}
                  countryCode={countryCode}
                  index={index}
                />
              </li>
            ))}
          </ul>
        ) : (
          <div className={styles.empty}>
            <p className={styles.emptyTitle}>{t('noBoutiques')}</p>
            <p className={styles.emptyHint}>{t('noBoutiquesHint')}</p>
          </div>
        )}
      </div>
    </section>
  );
}

BoutiquesList.displayName = 'BoutiquesList';
